import { AdditiveBlending, Color, Mesh, PlaneGeometry, ShaderMaterial, Vector2 } from 'three';

import { simpleVs } from '@resn/gozer-three';
import { gsap } from '@resn/gsap';

import fragmentShader from './shaders/shine.frag';

class ShineLayer extends Mesh {
    constructor(parent) {
        // Create background quad with shine shader
        const geometry = new PlaneGeometry(1, 1);
        const material = new ShaderMaterial({
            vertexShader: simpleVs,
            fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uAspect: { value: 1 },
                uLightPosition: { value: new Vector2(0.5, 0.5) },
                uLightIntensity: { value: 0.6 },
                // uRayCount: { value: 12.0 },
                uRaySpeed: { value: 1 },
                uLargeVariation: { value: 1.0 },
                lightColor: { value: new Color('rgb(212, 183, 124)') },
                uGammaBorder: { value: 1 },
                uUVScale: { value: new Vector2(2, 1.4) },
                uOpacity: { value: 1.0 }, // Add opacity uniform for fading
            },
            transparent: true, // Enable transparency
            depthWrite: false,
            depthTest: false,
            name: 'GoldenShineMaterial',
            blending: AdditiveBlending, // Use additive blending for a brighter effect
        });

        super(geometry, material);
        this.name = 'GoldenShine';
        this.renderOrder = -1;
        this.visible = false; // Start hidden
        this.tl = null; // For storing animation timeline
        parent.add(this);

        this.scale.setScalar(4);

        this.uniforms = material.uniforms;
    }

    animate({ time }) {
        this.uniforms.uTime.value = time * 1.2;
    }

    // Show the shine effect with animation
    show({ duration = 1.0, delay = 0, intensity = 1.0, ease = 'power2.out' } = {}) {
        // Kill any existing animations
        if (this.tl) {
            this.tl.kill();
        }

        // Make sure we're visible
        this.visible = true;

        // Create new animation timeline
        this.tl = gsap
            .timeline()
            .to(this.uniforms.uLightIntensity, { value: intensity, duration, delay, ease })
            .to(this.uniforms.uOpacity, { value: 1.0, duration: duration * 0.7, delay, ease }, 0);

        return this.tl;
    }

    // Hide the shine effect with animation
    hide({ duration = 0.5, delay = 0, ease = 'power2.in' } = {}) {
        // Create new animation timeline
        this.tl?.kill();
        this.tl = gsap
            .timeline({
                onComplete: () => (this.visible = false),
            })
            .to(this.uniforms.uLightIntensity, { value: 0, duration, delay, ease })
            .to(this.uniforms.uOpacity, { value: 0, duration: duration * 0.7, delay, ease }, 0);

        return this.tl;
    }

    // Special effect for the reveal moment
    reveal({ intensity = 2.0, duration = 1, delay = 0 } = {}) {
        // First boost intensity quickly, then ease back to normal
        this.tl?.kill();
        this.tl = gsap
            .timeline()
            .to(this.uniforms.uLightIntensity, {
                value: intensity,
                duration: duration * 0.3,
                delay,
                ease: 'power2.out',
            })
            .to(this.uniforms.uLightIntensity, { value: 1, duration: duration * 0.7, delay });

        return this.tl;
    }

    // Flash effect for emphasis
    flash({ intensity = 2.5, duration = 0.5, delay = 0 } = {}) {
        // Quick flash up and back
        this.tl?.kill();
        this.tl = gsap
            .timeline()
            .to(this.uniforms.uLightIntensity, {
                value: intensity,
                duration: duration * 0.3,
                delay,
                ease: 'power2.out',
            })
            .to(this.uniforms.uLightIntensity, {
                value: 0.6,
                duration: duration * 0.7,
                ease: 'sine.out',
            });

        return this.tl;
    }

    // Update aspect ratio on window resize
    updateAspect(width, height) {
        if (this.material) {
            this.uniforms.uAspect.value = width / height;
        }
    }

    // Clean up resources
    dispose() {
        if (this.tl) {
            this.tl.kill();
            this.tl = null;
        }
        if (this.geometry) this.geometry.dispose();
        if (this.material) this.material.dispose();
        if (this.parent) this.parent.remove(this);
    }

    reset() {
        this.position.z = -0.2;
        this.visible = false;
        this.material.uniforms.uLightIntensity.value = 0;
        this.material.uniforms.uOpacity.value = 0;
        this.tl?.kill();
    }
}

export default ShineLayer;
